import { useCallback, useEffect } from 'react';
import {
  STAGE_CODE_PARAM,
  STAGE_SUB_PARAM,
  resolveStageCode,
} from '../utils/stageCode';
import { TAB_PARAM, FIXTURES_TAB } from '../utils/tabs';

const MATCH_ID_PARAM = 'matchId';

const STANDINGS_TAB = 'standings';
const COUNTRIES_TAB = 'countries';
const STANDINGS_GROUP_STAGE = 'group-stage';
const STANDINGS_RANKING = 'ranking';
const SCREEN_PARAM = 'screen';
const COUNTRY_ID_PARAM = 'countryId';
const COUNTRY_SUB_TAB_PARAM = 'subTab';
const RETURN_COUNTRY_ID_PARAM = 'returnCountryId';
const FROM_PARAM = 'from';
const VIEW_FILTER_PARAM = 'viewFilter';
const LETTER_FILTER_PARAM = 'letterFilter';

const PARAMS_TO_CLEAR_ON_TAB_CHANGE = [
  MATCH_ID_PARAM,
  STAGE_CODE_PARAM,
  STAGE_SUB_PARAM,
  SCREEN_PARAM,
  COUNTRY_ID_PARAM,
  COUNTRY_SUB_TAB_PARAM,
  RETURN_COUNTRY_ID_PARAM,
  FROM_PARAM,
  VIEW_FILTER_PARAM,
  LETTER_FILTER_PARAM,
];

const ALWAYS_RESET_SCROLL_SELECTORS = [
  '.content_div',
  '[data-role="mundial2026-tabpanel"]',
  '[data-role="wc-statistics-sections"]',
  '[data-role="wc-country-detail"]',
];

const SCROLL_SELECTORS_BY_TAB = {
  [STANDINGS_TAB]: ['[data-role="wc-standings-view"]'],
};

const resetScrollBySelectors = selectors => {
  selectors.forEach(selector => {
    const el = document.querySelector(selector);
    if (el) el.scrollTop = 0;
  });
};

const resetScrollForTab = tabId => {
  const selectors = SCROLL_SELECTORS_BY_TAB[tabId];
  if (!selectors) return;
  resetScrollBySelectors(selectors);
};

const TAB_PARAM_HANDLERS = {
  [FIXTURES_TAB]: (params, { currentStage }) => {
    if (currentStage.fetched && currentStage.stage?.code) {
      params.set(STAGE_CODE_PARAM, resolveStageCode(currentStage.stage.code));
    }
  },
  [STANDINGS_TAB]: (params, { currentStage }) => {
    const stageCode = currentStage.stage?.code;
    const screen =
      stageCode === 'GROUP_STAGE' || stageCode === 'PHASE_GROUP'
        ? STANDINGS_GROUP_STAGE
        : STANDINGS_RANKING;

    params.set(SCREEN_PARAM, screen);
  },
  [COUNTRIES_TAB]: params => {
    params.delete(COUNTRY_ID_PARAM);
    params.delete(COUNTRY_SUB_TAB_PARAM);
  },
};

const resetTabParams = params => {
  PARAMS_TO_CLEAR_ON_TAB_CHANGE.forEach(key => params.delete(key));
};

const applyTabParams = (tabId, params, ctx) => {
  resetTabParams(params);

  const handler = TAB_PARAM_HANDLERS[tabId];
  if (handler) {
    handler(params, ctx);
  }
};

const applyInitialTabParams = (tabId, params, ctx) => {
  if (tabId !== FIXTURES_TAB || params.get(STAGE_CODE_PARAM)) return;

  const handler = TAB_PARAM_HANDLERS[tabId];
  if (handler) {
    handler(params, ctx);
  }
};

export const useTabChange = ({
  displayedTab,
  setSearchParams,
  currentStage,
}) => {

  useEffect(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        const currentTab = next.get(TAB_PARAM) || FIXTURES_TAB;

        if (!next.get(TAB_PARAM)) {
          next.set(TAB_PARAM, FIXTURES_TAB);
        }

        applyInitialTabParams(currentTab, next, { currentStage });
        return next;
      },
      { replace: true },
    );
  }, [currentStage.stage]);

  return useCallback(
    tabId => {
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0 });
      }

      if (typeof document !== 'undefined') {
        resetScrollBySelectors(ALWAYS_RESET_SCROLL_SELECTORS);
        resetScrollForTab(tabId);
      }

      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set(TAB_PARAM, tabId);
          applyTabParams(tabId, next, { currentStage });
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, currentStage.fetched, currentStage.stage],
  );
};

export default useTabChange;
